import { FLAG_TESTER } from '@/constants/featureFlags';

/**
 * Parses the ?flag_tester= query param into individual flag keys.
 * Repeated params and comma lists are equivalent: ?flag_tester=a,b&flag_tester=c → ['a','b','c'].
 */
export function getFlagTesterKeys(searchParams: URLSearchParams): string[] {
	return searchParams
		.getAll(FLAG_TESTER)
		.flatMap((value) => value.split(','))
		.map((key) => key.trim())
		.filter(Boolean);
}

/**
 * Serializes the parsed keys into the person-property value PostHog matches with icontains.
 * Returns null when the param is absent or names no flags, meaning omit the property entirely.
 */
export function getFlagTesterValue(searchParams: URLSearchParams): string | null {
	const keys = getFlagTesterKeys(searchParams);
	return keys.length > 0 ? keys.join(',') : null;
}

/** `?qa=true`: no split-url routing and no exposure. Edge and client read this one place so they agree. */
export function isQaRequest(searchParams: URLSearchParams): boolean {
	return searchParams.get('qa') === 'true';
}

/**
 * Latched until the next full page load — the same lifetime as the override itself. `_app.tsx`
 * sets the `flag_tester` person property once inside `posthog.init`, so a client-side route change
 * drops the param from the URL while the override stays in force. Reading only the live URL would
 * un-suppress exposures that are still QA-forced: SingleForm pushes /landing?flag_tester=... to
 * /thank-you, where those flags are read.
 */
let hasSeenFlagTester = false;

/** Test hook: the module-level latch would otherwise leak between test cases. */
export function resetFlagTesterLatch(): void {
	hasSeenFlagTester = false;
}

/**
 * True when flag values are QA-forced rather than genuinely evaluated. Callers suppress
 * `$feature_flag_called` on it, so forced values never land in experiment results.
 */
export function isFlagTesterActive(): boolean {
	if (hasSeenFlagTester) return true;

	const search = typeof window === 'undefined' ? undefined : window.location?.search;
	if (!search) return false;
	if (getFlagTesterValue(new URLSearchParams(search)) === null) return false;

	hasSeenFlagTester = true;
	return true;
}
